
import { useCallback } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useTokens } from "@/hooks/use-tokens";
import { useAdminStatus } from "@/hooks/use-admin-status";
import { toast } from "sonner";

import type { UseFormSubmissionProps, FormSubmissionState } from "./form-submission/types";
import { 
  validateAuthentication, 
  validateTokens, 
  validateInput 
} from "./form-submission/validation";
import { 
  processVoiceInput, 
  handleImageProcessing, 
  handleAudioPlayback 
} from "./form-submission/audio-processing";
import { 
  sendAIRequest, 
  createUserMessage, 
  createAssistantMessage, 
  generateMessageForAI 
} from "./form-submission/ai-request";
import { performCleanup } from "./form-submission/cleanup";

export function useFormSubmission({
  voiceRecording,
  voiceSettings,
  audioPlayback,
  imageHandling,
  addMessage,
  setIsLoading
}: UseFormSubmissionProps) {
  const { user, isAuthenticated } = useAuth();
  const { useToken, hasEnoughTokens } = useTokens();
  const { isAdmin } = useAdminStatus();

  const handleSubmit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Validation initiale
    if (!validateAuthentication(isAuthenticated, user)) return;

    console.log("=== Démarrage Soumission Formulaire ===");
    const currentVoiceSettings = voiceSettings.getCurrentSettings();
    console.log("Paramètres vocaux actuels:", {
      autoVoiceResponse: currentVoiceSettings.autoVoiceResponse,
      language: currentVoiceSettings.preferredLanguage,
      useElevenLabs: currentVoiceSettings.useElevenLabs,
      voiceOnlyMode: currentVoiceSettings.voiceOnlyMode
    });

    if (!validateTokens(isAdmin, hasEnoughTokens)) return;

    const formData = new FormData(e.currentTarget);
    let userInput = formData.get('message') as string;

    // Variables pour le nettoyage différé
    const state: FormSubmissionState = {
      shouldResetForm: false,
      imageToRemove: null,
      audioToRemove: null
    };

    try {
      setIsLoading(true);
      audioPlayback.stopAudio();
      
      // Gestion des tokens
      if (!isAdmin) {
        const tokenUsed = await useToken(1);
        if (!tokenUsed) {
          toast.error("🎁 Token insuffisant. Faites un don via Ko-fi pour obtenir plus de tokens.");
          return;
        }
        console.log("✅ Token utilisé avec succès");
      }
      
      // Traitement de l'audio vocal
      userInput = await processVoiceInput(
        voiceRecording, 
        userInput, 
        currentVoiceSettings.preferredLanguage
      );
      
      if (!validateInput(userInput, imageHandling.selectedImage)) return;

      // Traitement de l'image
      const imageDataUrl = await handleImageProcessing(imageHandling.selectedImage);
      
      // Création du message utilisateur
      const userMessage = createUserMessage(
        userInput, 
        imageHandling.selectedImage, 
        imageHandling
      );
      addMessage(userMessage);

      // Génération du message pour l'IA
      const messageForAI = generateMessageForAI(
        userInput, 
        imageHandling.selectedImage, 
        currentVoiceSettings.preferredLanguage
      );

      // Envoi de la requête à l'IA
      const response = await sendAIRequest(
        messageForAI,
        currentVoiceSettings.preferredLanguage,
        imageDataUrl,
        currentVoiceSettings
      );

      // Création et ajout du message assistant
      const assistantMessage = createAssistantMessage(
        response, 
        !!imageHandling.selectedImage
      );
      addMessage(assistantMessage);
      
      // Marquer pour nettoyage différé SEULEMENT après succès complet
      state.shouldResetForm = true;
      state.imageToRemove = imageHandling.selectedImage;
      state.audioToRemove = voiceRecording.recordedAudioRef.current;
      
      // Message de succès immédiat
      toast.success("Réponse reçue de l'assistant médical");
      
      // Gestion audio séparée avec sa propre gestion d'erreur
      handleAudioPlayback(
        currentVoiceSettings.autoVoiceResponse,
        audioPlayback,
        response,
        currentVoiceSettings.useElevenLabs,
        currentVoiceSettings.preferredLanguage
      );

    } catch (error) {
      console.error("❌ Erreur soumission:", error);
      toast.error("Erreur inattendue lors de l'envoi");
    } finally {
      setIsLoading(false);
      
      // Nettoyage différé pour éviter les conflits
      if (state.shouldResetForm) {
        performCleanup(
          e.currentTarget,
          state.imageToRemove,
          state.audioToRemove,
          imageHandling,
          voiceRecording
        );
      }
    }
  }, [
    user, isAuthenticated, useToken, hasEnoughTokens, isAdmin, 
    voiceRecording, voiceSettings, audioPlayback, imageHandling, 
    addMessage, setIsLoading
  ]);

  return { handleSubmit };
}
